// ReactBits-style primitives — vendored minimal subset (SpotlightCard, Aurora, SplitText reveal)
// Source dna: https://reactbits.dev (MIT-licensed patterns, reimplemented minimal, no dep on their SSG package)
import { useEffect, useRef, useState } from 'react'

// Aurora background — soft radial gradient blobs drifting slowly
export function AuroraBackground({ children, className = '' }) {
  return (
    <div className={`aurora-fixed ${className}`} aria-hidden="true">
      <div className="aurora-blob aurora-blob-1" />
      <div className="aurora-blob aurora-blob-2" />
      <div className="aurora-blob aurora-blob-3" />
      {children}
    </div>
  )
}

// SpotlightCard — border glow following the cursor (ReactBits SpotlightCard)
export function SpotlightCard({ children, className = '', spotlightColor = 'rgba(99,102,241,0.18)', ...props }) {
  const ref = useRef(null)

  function handleMouseMove(e) {
    const el = ref.current
    if (!el) return
    const r = el.getBoundingClientRect()
    el.style.setProperty('--mx', `${e.clientX - r.left}px`)
    el.style.setProperty('--my', `${e.clientY - r.top}px`)
  }

  return (
    <div
      ref={ref}
      onMouseMove={handleMouseMove}
      className={`spotlight-card ${className}`}
      style={{ '--spotlight': spotlightColor }}
      {...props}
    >
      <div className="spotlight-card-glow" />
      {children}
    </div>
  )
}

function useInView(threshold = 0.3) {
  const ref = useRef(null)
  const [seen, setSeen] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setSeen(true)
        io.disconnect()
      }
    }, { threshold })
    io.observe(el)
    return () => io.disconnect()
  }, [threshold])
  return [ref, seen]
}

// SplitText — character reveal on enter viewport (ReactBits SplitText)
export function SplitText({ text, className = '', delay = 0, stagger = 0.03 }) {
  const [ref, seen] = useInView(0.2)
  return (
    <span ref={ref} className={`split-text ${className}`} aria-label={text}>
      {text.split('').map((ch, i) => (
        <span
          key={i}
          className={`split-char ${seen ? 'split-char-in' : ''}`}
          style={{ transitionDelay: `${delay + i * stagger}s` }}
          aria-hidden="true"
        >
          {ch === ' ' ? '\u00A0' : ch}
        </span>
      ))}
    </span>
  )
}
